import { useState, useCallback } from 'react';

export interface HandshakeRecord {
  from: string;
  to: string;
  similarity: number;
}

function getStoreKey(teamId: string, suffix: string): string {
  return `team_${suffix}_${teamId}`;
}

export function useTeamStore(teamId: string) {
  const [claims, setClaims] = useState<Record<string, string>>(() => {
    try {
      const raw = localStorage.getItem(getStoreKey(teamId, 'claims'));
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const [handshakes, setHandshakes] = useState<HandshakeRecord[]>(() => {
    try {
      const raw = localStorage.getItem(getStoreKey(teamId, 'handshakes'));
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [myClaimedMemberId, setMyClaimedMemberId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(getStoreKey(teamId, 'myClaim'));
    } catch {
      return null;
    }
  });

  const persistClaims = useCallback((next: Record<string, string>) => {
    setClaims(next);
    try {
      localStorage.setItem(getStoreKey(teamId, 'claims'), JSON.stringify(next));
    } catch { /* ignore */ }
  }, [teamId]);

  const persistHandshakes = useCallback((next: HandshakeRecord[]) => {
    setHandshakes(next);
    try {
      localStorage.setItem(getStoreKey(teamId, 'handshakes'), JSON.stringify(next));
    } catch { /* ignore */ }
  }, [teamId]);

  const setMyClaim = useCallback((memberId: string | null) => {
    setMyClaimedMemberId(memberId);
    try {
      if (memberId) {
        localStorage.setItem(getStoreKey(teamId, 'myClaim'), memberId);
      } else {
        localStorage.removeItem(getStoreKey(teamId, 'myClaim'));
      }
    } catch { /* ignore */ }
  }, [teamId]);

  const claimCard = useCallback((memberId: string, nickname: string) => {
    const next = { ...claims, [memberId]: nickname };
    persistClaims(next);
    setMyClaim(memberId);
  }, [claims, persistClaims, setMyClaim]);

  const addHandshake = useCallback((record: HandshakeRecord) => {
    const exists = handshakes.some(h => h.from === record.from && h.to === record.to);
    if (exists) return;
    const next = [...handshakes, record];
    persistHandshakes(next);
  }, [handshakes, persistHandshakes]);

  const isClaimedByMe = useCallback((memberId: string) => {
    return myClaimedMemberId === memberId;
  }, [myClaimedMemberId]);

  const getMyHandshakesWith = useCallback((otherMemberId: string): HandshakeRecord | undefined => {
    if (!myClaimedMemberId) return undefined;
    return handshakes.find(h => h.from === myClaimedMemberId && h.to === otherMemberId);
  }, [handshakes, myClaimedMemberId]);

  const isHandshakeWithMe = useCallback((otherMemberId: string): HandshakeRecord | undefined => {
    if (!myClaimedMemberId) return undefined;
    return handshakes.find(h => h.from === otherMemberId && h.to === myClaimedMemberId);
  }, [handshakes, myClaimedMemberId]);

  return {
    claims,
    handshakes,
    myClaimedMemberId,
    claimCard,
    addHandshake,
    isClaimedByMe,
    getMyHandshakesWith,
    isHandshakeWithMe,
    setMyClaim,
  };
}
